// BuildingRenderStats.ts — Buildings Set1 §8 (diagnostics): a renderer-thread singleton with
// per-building mesh counts, written by `Building3D.tsx` on mount/unmount and read by the map-UI
// Stats panel (mirrors `sceneryRenderStats`/`TerrainProcessStats`/`mapRenderStats`). Lets the Stats
// panel answer "are any building meshes actually being produced, and how many room/floor meshes
// are attached right now?" without reaching into the three.js scene graph.
//
// The counts are what a user debugging "no building mesh appears" needs:
//   - buildingCount — how many `Building3D` mounts are live (one per renderable `Mod_Building`).
//   - meshCount — how many `THREE.Mesh` are currently attached to the scene for the building
//     (opaque wall/floor/ceiling meshes + transparent window-glass meshes; roof counted
//     separately via `roofCount`).
//   - roomCount / floorCount — the resolved room-instance / floor totals (from the §3.7 plan).
//   - geometryKeyCount — the number of distinct SHARED BufferGeometries (§6.4); if rooms exist
//     but this is 0, geometry production failed upstream of mounting.
// Per-building entries keyed by MapObject.id; deleted on unmount so totals stay accurate across
// reactive re-renders.
import {O} from "web-vcore";

export type BuildingMeshStats = {
	/** # THREE.Mesh currently attached to the scene for this building (opaque + glass). */
	meshCount: number;
	/** # THREE.Mesh for this building's roof (0 or 1). */
	roofCount: number;
	/** # resolved floors in the §3.7 geometry plan. */
	floorCount: number;
	/** # resolved room instances across all floors. */
	roomCount: number;
	/** # distinct shared BufferGeometries (§6.4). */
	geometryKeyCount: number;
};

class BuildingRenderStatsStore {
	/** Per-building mesh stats, keyed by MapObject.id. Empty ⇒ no building meshes mounted. */
	@O accessor buildings = new Map<string, BuildingMeshStats>();
}

export const buildingRenderStats = new BuildingRenderStatsStore();
